import type { ReactNode } from "react";
import { FileArchive } from "lucide-react";
import type { BackupSummary } from "@/lib/callables";
import { formatBytes } from "@/lib/dataTransfer";
import { formatCount, formatDateTime } from "@/lib/format";
import { CollectionCounts } from "./CollectionCounts";

/**
 * O que um ZIP de dados tem: o Firestore em documentos e coleções, o Storage
 * em arquivos e bytes, e de onde e quando ele saiu. `children` traz os avisos
 * de quem mostra (outro projeto, ZIP vazio).
 */
export function BackupSummaryView({
  summary,
  fileName,
  zipBytes,
  children,
}: {
  summary: BackupSummary;
  fileName: string;
  zipBytes: number;
  children?: ReactNode;
}) {
  return (
    <div className="rounded-md border border-border p-3">
      <p className="flex items-center gap-2 text-small font-semibold">
        <FileArchive className="size-4 shrink-0 text-primary" />
        <span className="truncate">{fileName}</span>
        <span className="shrink-0 font-normal text-muted-foreground">{formatBytes(zipBytes)}</span>
      </p>
      <dl className="mt-2 grid grid-cols-1 gap-2 xs:grid-cols-2">
        <div>
          <dt className="text-caption text-muted-foreground">Firestore</dt>
          <dd className="text-small font-semibold">
            {formatCount(summary.firestore.documents, "documento", "documentos")} em{" "}
            {formatCount(summary.firestore.collections, "coleção", "coleções")}
          </dd>
        </div>
        <div>
          <dt className="text-caption text-muted-foreground">Storage</dt>
          <dd className="text-small font-semibold">
            {formatCount(summary.storage.files, "arquivo", "arquivos")} (
            {formatBytes(summary.storage.bytes)})
          </dd>
        </div>
      </dl>
      <p className="mt-2 break-words text-caption text-muted-foreground">
        Exportado em {formatDateTime(summary.exportedAt)}
        {summary.projectId ? `, do projeto ${summary.projectId}` : ""}.
      </p>
      {Object.keys(summary.firestore.perCollection).length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-caption font-semibold">
            Documentos por coleção
          </summary>
          <CollectionCounts perCollection={summary.firestore.perCollection} />
        </details>
      ) : null}
      {children}
    </div>
  );
}
